import { createFileRoute } from '@tanstack/react-router';
import dedent from 'dedent';
import { Clipboard, ExternalLink, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type DemoShiftConfig = {
    readonly start_time_text?: string;
    readonly end_time_text?: string;
    readonly city?: string;
    readonly state?: string;
    readonly location?: string;
    readonly pay_rate_text?: string;
    readonly role_required_text?: string;
    readonly patient_first_name?: string;
};

type DemoConfig = {
    readonly instructions?: string;
    readonly shift?: DemoShiftConfig;
};

const DEFAULT_DEMO_CONFIG: DemoConfig = {
    instructions: '',
    shift: {
        start_time_text: '9:00 AM',
        end_time_text: '5:00 PM',
        city: 'Springfield',
        state: 'MA',
        location: '123 Main Street, Springfield',
        pay_rate_text: '$25/hour',
        role_required_text: 'Certified Nursing Assistant',
        patient_first_name: 'John',
    },
};

export const Route = createFileRoute('/_app/(main)/training/demo')({
    component: DemoTab,
});

function DemoTab() {
    const [copied, setCopied] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);
    const [showConfig, setShowConfig] = useState(false);
    const [demoConfig, setDemoConfig] =
        useState<DemoConfig>(DEFAULT_DEMO_CONFIG);
    const [isSaving, setIsSaving] = useState(false);
    const organizationId = useOrganizationId();
    const { data: organization, mutate: mutateOrganization } = useOrganization(
        organizationId,
        { reason: 'training' },
    );

    useEffect(() => {
        if (organization?.demo_config) {
            setDemoConfig(organization.demo_config as DemoConfig);
        }
    }, [organization]);

    const demoUrl = organization
        ? `${window.location.origin}/${organization.id}/demo`
        : '';

    const handleCopy = async () => {
        if (demoUrl) {
            await navigator.clipboard.writeText(demoUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleCopyEmail = async () => {
        const emailTemplate = dedent`
            Hi [Staff Name],

            We're excited to introduce you to our new AI voice assistant that will help with shift scheduling. This AI assistant will call you to confirm shifts, offer new opportunities, and answer questions about your schedule.

            To see how it works, click the link below to receive a call:

            ${demoUrl}

            Best regards,
            [Your Name]
        `;

        await navigator.clipboard.writeText(emailTemplate);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 2000);
    };

    const handleSaveConfig = async () => {
        if (!organizationId) {
            return;
        }

        setIsSaving(true);
        try {
            await getApiClient().put({
                url: '/organizations/{organization_id}/demo-config',
                pathParams: {
                    organization_id: organizationId,
                },
                body: demoConfig,
            });
            await mutateOrganization();
            setShowConfig(false);
        } catch (error) {
            console.error('Failed to save demo config:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="min-w-0 space-y-4 sm:space-y-6">
            {/* Send Caregivers Voice Demo Card */}
            <div className="border-border bg-background min-w-0 rounded-lg border p-4 sm:p-6">
                <div className="mb-3 flex min-w-0 flex-col gap-3 sm:mb-2 sm:flex-row sm:items-start sm:justify-between">
                    <h2 className="min-w-0 text-xl font-semibold">
                        Your Custom Self-Serve Demo Page
                    </h2>
                    <div className="grid grid-cols-1 gap-2 sm:flex sm:shrink-0">
                        {/* Configure Demo Button */}
                        <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => setShowConfig(!showConfig)}
                        >
                            <Settings className="mr-2 h-4 w-4" />
                            Configure Demo
                        </Button>
                        {/* View Demo Button */}
                        <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => window.open(demoUrl, '_blank')}
                            disabled={!demoUrl}
                        >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View Demo Page
                        </Button>
                    </div>
                </div>
                <p className="text-muted-foreground mb-6 text-base sm:mb-8">
                    Send your staff a self-serve page where they can request a
                    Phoebe test call whenever they want
                </p>
                {/* Demo Configuration Form */}
                {showConfig && (
                    <div className="border-border bg-muted/50 mb-8 min-w-0 rounded-lg border p-4">
                        <h3 className="mb-4 text-lg font-semibold">
                            Demo Configuration
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="instructions">
                                    Agent Instructions
                                </Label>
                                <Textarea
                                    id="instructions"
                                    placeholder="Custom instructions for the demo call agent..."
                                    value={demoConfig.instructions || ''}
                                    onChange={(e) =>
                                        setDemoConfig({
                                            ...demoConfig,
                                            instructions: e.target.value,
                                        })
                                    }
                                    className="mt-1"
                                    rows={3}
                                />
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <Label htmlFor="role">Role Required</Label>
                                    <Input
                                        id="role"
                                        placeholder="Certified Nursing Assistant"
                                        value={
                                            demoConfig.shift
                                                ?.role_required_text || ''
                                        }
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    role_required_text:
                                                        e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="start_time">
                                        Start Time
                                    </Label>
                                    <Input
                                        id="start_time"
                                        placeholder="9:00 AM"
                                        value={
                                            demoConfig.shift?.start_time_text ||
                                            ''
                                        }
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    start_time_text:
                                                        e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="end_time">End Time</Label>
                                    <Input
                                        id="end_time"
                                        placeholder="5:00 PM"
                                        value={
                                            demoConfig.shift?.end_time_text ||
                                            ''
                                        }
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    end_time_text:
                                                        e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                        id="city"
                                        placeholder="Springfield"
                                        value={demoConfig.shift?.city || ''}
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    city: e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="state">State</Label>
                                    <Input
                                        id="state"
                                        placeholder="MA"
                                        value={demoConfig.shift?.state || ''}
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    state: e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="location">Location</Label>
                                    <Input
                                        id="location"
                                        placeholder="123 Main Street"
                                        value={demoConfig.shift?.location || ''}
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    location: e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="pay_rate">Pay Rate</Label>
                                    <Input
                                        id="pay_rate"
                                        placeholder="$25/hour"
                                        value={
                                            demoConfig.shift?.pay_rate_text ||
                                            ''
                                        }
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    pay_rate_text:
                                                        e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="patient_name">
                                        Patient First Name
                                    </Label>
                                    <Input
                                        id="patient_name"
                                        placeholder="John"
                                        value={
                                            demoConfig.shift
                                                ?.patient_first_name || ''
                                        }
                                        onChange={(e) =>
                                            setDemoConfig({
                                                ...demoConfig,
                                                shift: {
                                                    ...demoConfig.shift,
                                                    patient_first_name:
                                                        e.target.value,
                                                },
                                            })
                                        }
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <Button
                                    variant="outline"
                                    className="w-full sm:w-auto"
                                    onClick={() => setShowConfig(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    className="w-full sm:w-auto"
                                    onClick={handleSaveConfig}
                                    disabled={isSaving}
                                >
                                    {isSaving
                                        ? 'Saving...'
                                        : 'Save Configuration'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                {/* Demo Link Input with Copy Button */}
                <div className="mb-8">
                    <p className="text-foreground mb-2 text-sm">
                        Share this link with your staff to get a call from
                        Phoebe:
                    </p>
                    <div className="mb-4 flex min-w-0 gap-2">
                        <Input
                            value={demoUrl}
                            readOnly={true}
                            className="min-w-0 flex-1"
                            placeholder="Loading demo link..."
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleCopy}
                            disabled={!demoUrl}
                        >
                            <Clipboard className="h-4 w-4" />
                        </Button>
                    </div>
                    {copied && (
                        <p className="mb-4 text-sm text-green-600">
                            Link copied to clipboard!
                        </p>
                    )}
                </div>
                {/* Email Template */}
                <p className="text-foreground mb-4 text-sm">
                    Here’s an example email you can send to your staff:
                </p>
                <div className="bg-muted/50 relative mb-4 min-w-0 rounded-lg border p-4 pb-14 sm:pb-4">
                    <p className="text-sm">Hi [Staff Name],</p>
                    <br />
                    <p className="text-sm">
                        We’re excited to introduce you to our new AI voice
                        assistant that will help with shift scheduling. This AI
                        assistant will call you to confirm shifts, offer new
                        opportunities, and answer questions about your schedule.
                    </p>
                    <br />
                    <p className="text-sm">
                        To see how it works, click the link below to receive a
                        call:
                    </p>
                    <br />
                    <p className="text-sm">
                        <a
                            href={demoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="break-all"
                        >
                            {demoUrl}
                        </a>
                    </p>
                    <br />
                    <p className="text-sm">
                        Best regards,
                        <br />
                        [Your Name]
                    </p>
                    {/* Copy button in bottom right */}
                    <Button
                        variant="outline"
                        size="icon"
                        className="bg-muted/50 absolute right-2 bottom-2"
                        onClick={handleCopyEmail}
                        disabled={!demoUrl}
                    >
                        <Clipboard className="h-4 w-4" />
                    </Button>
                </div>
                {emailCopied && (
                    <p className="mb-4 text-sm text-green-600">
                        Email template copied to clipboard!
                    </p>
                )}
            </div>
        </div>
    );
}
